import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Mail, Lock, ArrowLeft, Menu, X } from 'lucide-react';
import { authService } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { getDashboardRoute } from '../utils/routeHelper';

const GarageLogin: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshUser, handleLoginSuccess } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Clear any existing auth state before attempting new login
    authService.clearAuthData();

    try {
      const response = await authService.garageLogin(formData.email, formData.password);
      
      console.log('Login API successful:', response.user);
      
      // Verify data is stored correctly
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      console.log('Post-login storage check:', { 
        hasToken: !!storedToken, 
        hasUser: !!storedUser,
        userRole: response.user.role 
      });
      
      console.log('Login successful, will update auth context for role:', response.user.role);
      
      // Update auth context immediately with user data
      handleLoginSuccess(response.user);
      
      console.log('Auth context updated, preparing for navigation');
      
      // Give context more time to update and ensure state is stable
      setTimeout(() => {
        console.log('GarageLogin: About to navigate to /dashboard');
        
        // Double-check auth state before navigation
        const authCheck = authService.isAuthenticated();
        const userCheck = authService.getCurrentUser();
        const storageCheck = {
          hasToken: !!localStorage.getItem('access_token'),
          hasUser: !!localStorage.getItem('user'),
          hasRefresh: !!localStorage.getItem('refresh_token')
        };
        
        console.log('Pre-navigation auth check:', { 
          authCheck, 
          userCheck: userCheck?.name, 
          userRole: userCheck?.role,
          storage: storageCheck
        });
        
        if (authCheck && userCheck && storageCheck.hasToken) {
          try {
            console.log('GarageLogin: All checks passed, navigating to dashboard');
            navigate('/dashboard', { replace: true });
            console.log('GarageLogin: navigate() called successfully');
          } catch (navError) {
            console.error('GarageLogin: Navigation error:', navError);
            const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                             window.location.pathname.startsWith('/dev') ? '/dev' : '';
            window.location.href = basename + '/dashboard';
          }
        } else {
          console.error('GarageLogin: Auth state invalid after login!', {
            authCheck,
            userCheck: !!userCheck,
            hasToken: storageCheck.hasToken
          });
          setError('Authentication state error. Please try again.');
        }
      }, 250);
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error;
      if (typeof errorMessage === 'string') {
        setError(errorMessage);
      } else if (typeof errorMessage === 'object' && errorMessage?.message) {
        setError(errorMessage.message);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold" style={{ color: '#008B8B' }}>Rayyi7</h1>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.features')}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.about')}
              </Link>
              <Link to="/pricing" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.pricing')}
              </Link>
              </div>

              <LanguageToggle />

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <Link 
                to="/#features" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.features')}
              </Link>
              <Link 
                to="/#about" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/pricing" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-64px)]">
        <div className="max-w-md w-full">
          {/* Back Button */}
          <button
            onClick={() => navigate('/login')}
            className="mb-8 flex items-center text-gray-600 hover:text-[#008B8B] transition"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('garageLogin.backToSelection')}
          </button>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="text-center mb-8">
              <div className="bg-[#008B8B] bg-opacity-10 p-4 rounded-full inline-block mb-4">
                <Building2 className="w-12 h-12" style={{ color: '#008B8B' }} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">{t('garageLogin.title')}</h2>
              <p className="text-gray-600 mt-2">{t('garageLogin.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('garageLogin.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-[#008B8B]"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('garageLogin.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-[#008B8B]"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input type="checkbox" className="h-4 w-4 text-[#008B8B] border-gray-300 rounded" />
                  <span className="ml-2 text-sm text-gray-600">{t('garageLogin.rememberMe')}</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-[#008B8B] hover:text-[#006666]">
                  {t('garageLogin.forgotPassword')}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 rounded-lg font-medium text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#FF8C00' }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#E67E00')}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#FF8C00')}
              >
                {loading ? t('garageLogin.signingIn') : t('garageLogin.signIn')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {t('garageLogin.noAccount')}{' '}
                <Link to="/register/garage" className="text-[#008B8B] hover:text-[#006666] font-medium">
                  {t('garageLogin.register')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GarageLogin;